import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Image } from "react-bootstrap";

import "./PayWall.scss";
import BattleImage from "../../images/Battle.png";
import PayWallBGImage from "../../images/paywall-bg.png";
import Icon01 from "../../images/paywall-icon-01.png";
import Icon02 from "../../images/paywall-icon-02.png";
import Icon03 from "../../images/paywall-icon-03.png";
import Icon04 from "../../images/paywall-icon-04.png";
import Icon05 from "../../images/paywall-icon-05.png";
import PrimaryActionButton from "../Buttons/PrimaryActionButton/PrimaryActionButton";
import {
  getPlanCopyKeys,
  usePaywallPricing,
} from "../../hooks/usePaywallPricing";
import { FunctionWithVoidReturn } from "../../utils/constants/generic-types";
import useAnalytics from "../../hooks/useAnalytics";
import { LINKS } from "../../utils/constants";

const FEATURES = [
  { icon: Icon01, key: "paywall.unlimited_access" },
  { icon: Icon02, key: "paywall.new_games" },
  { icon: Icon03, key: "paywall.invite_battle" },
  { icon: Icon04, key: "paywall.all_devices" },
  { icon: Icon05, key: "paywall.no_ads" },
] as const;

interface PayWallProps {
  onCtaClick?: () => void;
  handleSuccessfulSubscription?: FunctionWithVoidReturn;
}

function PayWall({ onCtaClick, handleSuccessfulSubscription }: PayWallProps) {
  const { t } = useTranslation("common");
  const analytics = useAnalytics();
  const { availablePackages, calculatePricing, purchase, isLoading } =
    usePaywallPricing();

  const [activePackageId, setActivePackageId] = useState<string | null>(null);

  useEffect(() => {
    if (!activePackageId && availablePackages.length > 0) {
      setActivePackageId(availablePackages[0].identifier);
    }
  }, [availablePackages, activePackageId]);

  const selectedPackage =
    availablePackages.find((p) => p.identifier === activePackageId) ??
    availablePackages[0] ??
    null;
  const pricing = calculatePricing(selectedPackage);
  const selectedCopy = getPlanCopyKeys(selectedPackage?.packageType);
  const selectedSubtitle =
    pricing.perMonth || (selectedCopy ? t(selectedCopy.subtitleKey) : "");
  const selectedBilling = selectedCopy ? t(selectedCopy.billingKey) : "";

  const handleCtaClick = async () => {
    if (!selectedPackage) return;
    try {
      await purchase(selectedPackage, () => {
        const fiatAmount = selectedPackage.webBillingProduct?.price?.amountMicros
          ? selectedPackage.webBillingProduct.price.amountMicros / 1_000_000
          : 0;
        analytics.sendSubscribeSuccessfulEvent({
          fiat_amount: fiatAmount,
          payment_option: selectedCopy ? t(selectedCopy.labelKey) : selectedPackage.webBillingProduct?.title,
          transaction_status: "successful",
        });
        handleSuccessfulSubscription?.();
      });
      onCtaClick?.();
    } catch {
    }
  };

  return (
    <div className="paywall">
      <div className="paywall-top">
        <Image
          src={PayWallBGImage}
          alt="paywall-bg"
          className="paywall-bg-image"
        />

        <p className="paywall-heading">
          {t("results_page.play_everything")}
          {t("punctuations.full_stop")}
          <br />
          {t("games:battle")}
          <Image
            src={BattleImage}
            alt="battle"
            className="paywall-inline-image"
          />
          {t("results_page.anyone")}
          {t("punctuations.full_stop")}
        </p>

        <ul className="paywall-features">
          {FEATURES.map(({ icon, key }) => (
            <li key={key} className="paywall-feature-item">
              <span className="paywall-feature-emoji">
                <Image src={icon} alt="" />
              </span>
              <span className="paywall-feature-text">{t(key)}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="paywall-bottom">
        <div className="paywall-switcher">
          {availablePackages.map((pkg) => {
            const planPricing = calculatePricing(pkg);
            const copy = getPlanCopyKeys(pkg.packageType);
            return (
              <button
                key={pkg.identifier}
                className={`paywall-switcher-option ${activePackageId === pkg.identifier ? "is-active" : ""}`}
                onClick={() => setActivePackageId(pkg.identifier)}
              >
                <span className="paywall-switcher-label">
                  {copy ? t(copy.labelKey) : pkg.webBillingProduct?.title}
                </span>
                {planPricing.discount && (
                  <span className="paywall-switcher-discount">
                    {planPricing.discount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="paywall-plan-card">
          <div className="paywall-plan-card-left">
            <span className="paywall-plan-title">
              {t("paywall.all_access")}
            </span>
            <span className="paywall-plan-subtitle">{selectedSubtitle}</span>
          </div>
          <div className="paywall-plan-card-right">
            <div className="paywall-plan-price-row">
              {pricing.originalPrice && (
                <span className="paywall-plan-original-price">
                  {pricing.originalPrice}
                </span>
              )}
              <span className="paywall-plan-price">{pricing.price}</span>
            </div>
            <span className="paywall-plan-billing">{selectedBilling}</span>
          </div>
        </div>

        <PrimaryActionButton
          text={isLoading ? "Processing..." : t("paywall.unlock_all_games")}
          onClick={handleCtaClick}
          buttonClassName="w-100"
          disabled={isLoading || !selectedPackage}
        />

        <p className="paywall-cancel-note">{t("paywall.cancel_anytime")}</p>

        <div className="paywall-footer-links">
          <button className="paywall-footer-link">
            {t("paywall.restore")}
          </button>
          <span className="paywall-footer-dot" />
          <a
            href={LINKS.WEBPAGE.TERMS_AND_CONDITIONS}
            target="_blank"
            rel="noopener noreferrer"
            className="paywall-footer-link"
          >
            {t("paywall.terms_of_service")}
          </a>
          <span className="paywall-footer-dot" />
          <a
            href={LINKS.WEBPAGE.PRIVACY_POLICY}
            target="_blank"
            rel="noopener noreferrer"
            className="paywall-footer-link"
          >
            {t("paywall.privacy_policy")}
          </a>
        </div>
      </div>
    </div>
  );
}

export default PayWall;
